/**
 * src/hooks/useDictation.js
 *
 * Click-to-toggle voice-to-text dictation. Records audio into ONE blob,
 * exposes a live RMS level for waveform rendering, and on stop posts the
 * blob to /api/ai?action=voice.transcribe_direct to get one transcript
 * in one round-trip.
 *
 * This is the simple composer-mic path. It does NOT go through the
 * chunked finalize_recording pipeline, so there is no 54-chunk 504 risk,
 * no Gemini quota exposure, no classifier, no DB write — just Whisper.
 * The chunked pipeline is still used by the conversation-mode voice
 * overlay (separate button, separate UX).
 *
 * DURABILITY CONTRACT (TICKET-324, CEO 2026-07-22) — the reason this file
 * is not just a fetch wrapper. A 5m11s email voice note was destroyed: the
 * blob lived only in `chunksRef`, the size guard tripped, a generic toast
 * fired, and the audio was gone. Now:
 *
 *   1. A draft row is opened in IndexedDB when recording STARTS, and the
 *      encoder flushes into it every TIMESLICE_MS — so a tab crash at
 *      minute 4 of a 5-minute note costs at most 15 seconds.
 *   2. The assembled blob is written to that draft BEFORE the transcribe
 *      POST, and deleted ONLY after real text comes back.
 *   3. Oversize / network-down / 4xx / 5xx / empty-transcript all leave the
 *      draft in place and surface it as `pending`, with retryPending() and
 *      downloadPending() as the two ways out. Nothing is dropped on the
 *      floor, and nothing is deleted without the user asking.
 *   4. Every failure branch emits to the observability spine. The prod
 *      incident produced ZERO rows anywhere — the missing emit WAS the bug.
 *
 * IndexedDB unavailable (Safari private mode, old browser)? Dictation still
 * works; it just isn't crash-proof, and `draftsUnavailable` says so.
 *
 * Public API:
 *   const {
 *     recording, transcribing, audioLevel, error,
 *     pending, draftsUnavailable,
 *     start, stop, toggle,
 *     retryPending, discardPending, downloadPending,
 *   } = useDictation({ onTranscript, onError, language })
 *
 *   - recording        — boolean, true while MediaRecorder is active
 *   - transcribing     — boolean, true while /api/ai is pending
 *   - audioLevel       — 0..1 RMS from the live mic, updated ~30x/sec
 *   - error            — string|null, last surfaced error (clears on next start)
 *   - pending          — {draft_id, size_bytes, mime_type, duration_sec, …}|null
 *                        a recording that survived a failed transcription
 *   - start() / stop() / toggle()
 *   - retryPending()   — re-send the saved audio
 *   - discardPending() — user explicitly throws the saved audio away
 *   - downloadPending()— save the audio to the device as a file
 *
 * TWO TRANSPORTS, one contract. Under 2.25MB of audio (~9m50s at 32kbps) the
 * blob rides the JSON body to voice.transcribe_direct — one round-trip, no
 * storage hop, fastest for the common case. Over that it would blow the
 * server's MAX_DIRECT_B64 base64 ceiling, so it goes to UVTP
 * (/api/voice-transcribe): signed upload, chunked heavy path, poll — no
 * body-size ceiling and no single-provider duration cap (TICKET-1134). Both transports share ONE keep-or-
 * delete tail, so the durability contract above is identical either way.
 * Hard stop at MAX_STORAGE_BYTES (the standard-upload cap); past that the
 * draft is kept and offered for download rather than silently dropped.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { getAuthHeaders } from '../lib/api/client';
import { registerMic, releaseMic } from '../lib/voice/micRegistry';
import { pollUvtpRun } from '../lib/voice/uvtpPoller.js';
import { useAgentMode } from '../lib/agent/agentModeContext.jsx';
import { logVoiceEvent } from '../lib/voice/voiceLogger.js';
import { pickRecorderMime } from '../lib/voice/audioGuards';
import { bytesToBase64 } from '../lib/utils/base64';
import {
  saveDictationDraft,
  updateDictationDraft,
  listDictationDrafts,
  getDictationDraft,
  deleteDictationDraft,
} from '../lib/voiceQueue.js';
import { rememberDictation } from '../lib/voice/dictationSafety.js';

const API = '/api/ai';
const UVTP = '/api/voice-transcribe';
const LEVEL_HZ = 30; // waveform updates per second

// Server-side ceiling, mirrored: lib/handlers/voice-v2.js MAX_DIRECT_B64.
// The guard MUST be expressed in the units the server checks. Comparing RAW
// bytes against a BASE64 cap is what let 2.25-3MB blobs pass the client and
// then 413 server-side with a message the user never saw (TICKET-324).
const MAX_DIRECT_B64 = 3 * 1024 * 1024;
const MAX_DIRECT_BYTES = Math.floor(MAX_DIRECT_B64 / 4) * 3; // 2,359,296

// Overflow transport ceiling. Supabase's STANDARD storage upload is capped
// (CEO 2026-05-31: a 58MB PUT was refused "exceeded the maximum allowed size");
// uploadVoiceFile switches to resumable/tus at 40MB. At 32kbps 40MB is ~2.8
// HOURS of speech, so the standard upload covers every conceivable dictation
// and there is no reason to drag tus into the composer path.
// ponytail: no resumable upload here. Add one only if a >2.8h dictation ever
// appears, which would be a different product than "compose an email".
const MAX_STORAGE_BYTES = 40 * 1024 * 1024;

// Browsers default MediaRecorder audio to 64-128kbps when you don't ask.
// That is what made a 5-minute note weigh 2.4-4.8MB and blow the direct-path
// budget. 32kbps opus is transparent for dictated speech and puts 5 minutes
// at ~1.2MB, 9m50s at the ceiling.
const AUDIO_BITS_PER_SECOND = 32000;

// Encoder flush cadence. Without a timeslice MediaRecorder holds everything
// in memory until stop() — a tab crash (or a composer UNMOUNT mid-note) loses
// it. TICKET-341: 15s was too coarse for the common case — a short AI note
// ("confirm 40m² Sunbrella, 3 weeks, 15% deposit") ends before the first slice
// ever fires, so NOTHING reaches IndexedDB and the "timeslice flushes are the
// salvage" assumption is false for exactly the notes people actually dictate.
// 3s means even a ~4s note lands at least one flush on disk. Opus @32kbps: the
// per-slice overhead is negligible.
const TIMESLICE_MS = 3000;

// TICKET-1079: one mime picker for every recorder (src/lib/voice/audioGuards.js).
const pickMime = pickRecorderMime;

async function blobToBase64(blob) {
  const buf = await blob.arrayBuffer();
  return bytesToBase64(new Uint8Array(buf));
}

function newDraftId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `dictation-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * IndexedDB round-trips can strip the Blob prototype (structured clone) — the
 * same hazard voiceQueue.getRecordingBlob documents. Rebuild before use.
 */
function reviveBlob(stored, mimeType) {
  if (!stored) return null;
  if (typeof Blob === 'undefined') return stored;
  return new Blob([stored], { type: mimeType || stored.type || 'audio/webm' });
}

// safetyScope (TICKET-936): a composer that passes one gets every transcript saved
// to the text safety store (src/lib/voice/dictationSafety.js) before the audio goes.
export default function useDictation({ onTranscript, onError, language = 'auto', safetyScope = null } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);
  // A recording that outlived a failed transcription. Non-null = there is
  // audio on this device that has not become text yet.
  const [pending, setPending] = useState(null);
  const [draftsUnavailable, setDraftsUnavailable] = useState(false);
  // TICKET-936: the recorder was stopped while the app was hidden (screen lock).
  const [interrupted, setInterrupted] = useState(false);

  // F4 (2026-04-21) — mutual exclusion. Entering 'capture' mode
  // overrides 'conversation' so any mounted VoiceModeOverlay hears
  // its mode flip and AgentShell can react (close the overlay).
  // Also gates useStreamingTTS.speak() — while capture is active,
  // any leftover conversation audio is refused by F3's gate.
  const { enterMode, exitMode } = useAgentMode();

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const levelTimerRef = useRef(null);
  const mimeRef = useRef(null);
  const draftIdRef = useRef(null);
  const startedAtRef = useRef(0);
  // Callbacks live in refs so the persistence paths below don't churn their
  // useCallback identities every render when a parent passes inline arrows.
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  onTranscriptRef.current = onTranscript;
  onErrorRef.current = onError;

  const emit = useCallback((step, status, message, meta = {}) => {
    try {
      logVoiceEvent(step, status, message, { source: 'client', module: 'voice', ...meta });
    } catch {
      // Telemetry must never break dictation.
    }
  }, []);

  /**
   * Every IndexedDB touch goes through here. A browser without IDB (Safari
   * private mode) degrades to the old volatile behaviour instead of throwing
   * — but says so loudly in telemetry and via `draftsUnavailable`, because
   * "we silently stopped protecting your audio" is the exact failure mode
   * this ticket exists to kill.
   */
  const withDrafts = useCallback(async (fn, label) => {
    try {
      const result = await fn();
      setDraftsUnavailable(false);
      return result;
    } catch (err) {
      setDraftsUnavailable(true);
      emit('dictation_draft_store_error', 'error',
        `dictation draft store unavailable (${label}): ${err?.message}`,
        { error_code: err?.name || null, reason: label });
      return null;
    }
  }, [emit]);

  const cleanupMeter = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (levelTimerRef.current) { clearTimeout(levelTimerRef.current); levelTimerRef.current = null; }
    if (analyserRef.current) { try { analyserRef.current.disconnect(); } catch { /* noop */ } analyserRef.current = null; }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      releaseMic(streamRef.current);
      streamRef.current = null;
    }
  }, []);

  const cleanupAll = useCallback(() => {
    cleanupMeter();
    cleanupStream();
    recorderRef.current = null;
    chunksRef.current = [];
  }, [cleanupMeter, cleanupStream]);

  // Unmount cleanup lives lower, right after flushDraft is defined — it now
  // salvages the in-flight recording and must reference flushDraft without
  // tripping its const temporal-dead-zone. See the effect below flushDraft.

  // Mount: adopt any draft left behind by a previous session — a crashed
  // tab, a failed transcription, a page the user navigated away from. This
  // is what makes the audio outlive the component that recorded it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const drafts = await withDrafts(() => listDictationDrafts(), 'adopt');
      if (cancelled || !drafts || drafts.length === 0) return;
      const newest = drafts[0];
      setPending(newest);
      emit('dictation_draft_adopted', 'ok',
        `recovered ${drafts.length} unsent dictation draft(s)`,
        { scope_id: newest.draft_id, bytes: newest.size_bytes ?? null });
    })();
    return () => { cancelled = true; };
  }, [withDrafts, emit]);

  const startMeter = useCallback((stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;

      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        // RMS around the 128 baseline.
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setAudioLevel(Math.min(1, rms * 1.6));
        levelTimerRef.current = setTimeout(() => {
          rafRef.current = requestAnimationFrame(tick);
        }, 1000 / LEVEL_HZ);
      };
      tick();
    } catch (err) {
      // Meter is cosmetic; failure should not block recording.
      console.warn('[useDictation] meter init failed:', err?.message);
    }
  }, []);

  /** Write what we have so far into the draft row. */
  const flushDraft = useCallback((extra = {}) => {
    const draftId = draftIdRef.current;
    if (!draftId) return null;
    const mime = mimeRef.current || 'audio/webm';
    let blob;
    try {
      blob = new Blob(chunksRef.current, { type: mime });
    } catch {
      return null;
    }
    return withDrafts(() => saveDictationDraft({
      draft_id: draftId,
      audio_blob: blob,
      mime_type: mime,
      size_bytes: blob.size ?? null,
      duration_sec: startedAtRef.current
        ? Math.round((Date.now() - startedAtRef.current) / 1000)
        : null,
      language,
      ...extra,
    }), 'flush');
  }, [withDrafts, language]);

  // Unmount: release mic + timers, release the agentMode 'capture' claim, and
  // — TICKET-341 (CEO 2026-07-23, "voice-to-text soft-fails, nothing gets
  // processed") — SALVAGE the in-flight recording before wiping it.
  //
  // The composer can unmount WHILE a note is still recording: an app-wide
  // reload() flips `loading`, the open deal detail early-returns its skeleton,
  // this cleanup runs. The old code called cleanupAll() straight away, which
  // clears chunksRef — throwing away the in-memory tail (the ONLY copy of a
  // sub-timeslice note) with no flush and no telemetry. Prod signature was
  // `dictation_started` with no follow-up event, then a SECOND
  // `dictation_started` under the same capture mode (the remount). Flush what
  // we have to the IndexedDB draft first — saveDictationDraft's write outlives
  // React unmount, so the remounted composer's adopt effect recovers it and
  // DictationRecoveryBar offers Retry instead of the note silently vanishing.
  useEffect(() => () => {
    // TICKET-637: Stop the recorder explicitly first, allowing the final
    // ondataavailable event to fire and populate chunksRef with any buffered
    // audio (especially important for short notes < 3s with no timeslices yet).
    const rec = recorderRef.current;
    if (rec && rec.state === 'recording') {
      // TICKET-936: a real MediaRecorder delivers the final chunk AFTER stop()
      // returns, by which time the refs below are wiped. Keep our own handles
      // and save the tail when it lands (the save outlives the unmount).
      const tailId = draftIdRef.current;
      const tailChunks = chunksRef.current;
      const tailMime = mimeRef.current || 'audio/webm';
      const tailStarted = startedAtRef.current;
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) tailChunks.push(e.data); };
      rec.onstop = () => {
        if (!tailId || tailChunks.length === 0) return;
        let blob;
        try { blob = new Blob(tailChunks, { type: tailMime }); } catch { return; }
        if (!blob.size) return;
        withDrafts(() => saveDictationDraft({
          draft_id: tailId, audio_blob: blob, mime_type: tailMime, size_bytes: blob.size,
          duration_sec: tailStarted ? Math.round((Date.now() - tailStarted) / 1000) : null,
          language, status: 'pending',
        }), 'unmount-tail');
        emit('dictation_unmount_salvage', 'ok', 'composer unmounted mid-recording — saved the final chunk',
          { scope_id: tailId, chunks: tailChunks.length, reason: 'tail' });
      };
      try { rec.stop(); } catch { /* noop */ }
    }

    // Now check and flush what was captured (including the final chunk).
    if (chunksRef.current.length > 0 && draftIdRef.current) {
      flushDraft({ status: 'pending' });
      emit('dictation_unmount_salvage', 'ok',
        'composer unmounted mid-recording — flushed audio to draft for recovery',
        { scope_id: draftIdRef.current, chunks: chunksRef.current.length });
    }

    // Clean up streams, timers, and clear refs.
    cleanupMeter();
    cleanupStream();
    recorderRef.current = null;
    chunksRef.current = [];
    exitMode('capture');
  }, [cleanupMeter, cleanupStream, exitMode, flushDraft, emit, withDrafts, language]);

  const start = useCallback(async () => {
    setError(null);
    setInterrupted(false);
    // Ref, not state: continueInterrupted() calls stop() then start() in one closure.
    if (recorderRef.current && recorderRef.current.state === 'recording') return;
    // F4 — claim capture mode BEFORE opening the mic. AgentShell
    // watches this transition and closes any open conversation overlay
    // so the two pipelines don't hear the same audio simultaneously.
    enterMode('capture');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      mimeRef.current = mime;
      const rec = new MediaRecorder(stream, {
        ...(mime ? { mimeType: mime } : {}),
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      // 2026-04-20 (session 3) — register with global sweep registry.
      registerMic(stream, rec, 'useDictation');
      chunksRef.current = [];
      draftIdRef.current = newDraftId();
      startedAtRef.current = Date.now();
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
          // Timeslice flush -> the copy on disk trails the live recording by
          // at most TIMESLICE_MS. This is the crash insurance.
          if (rec.state === 'recording') flushDraft({ status: 'recording' });
          // TICKET-936: stopped from outside (screen lock -> mic registry). The
          // tail lands after stop(); our own stop()/unmount set onstop first.
          else if (!rec.onstop) flushDraft({ status: 'pending' });
        }
      };
      rec.onerror = (e) => {
        const msg = e?.error?.message || 'Recorder error';
        setError(msg);
        emit('dictation_failed', 'error', `MediaRecorder error: ${msg}`,
          { error_code: e?.error?.name || null, reason: 'recorder_error' });
        flushDraft({ status: 'pending', error_message: msg });
        if (onErrorRef.current) onErrorRef.current(msg);
      };
      recorderRef.current = rec;
      rec.start(TIMESLICE_MS);
      setRecording(true);
      startMeter(stream);
      emit('dictation_started', 'ok', 'composer dictation started',
        { scope_id: draftIdRef.current, mime });
    } catch (err) {
      const msg = err?.message || 'Microphone permission denied';
      setError(msg);
      emit('dictation_failed', 'error', `getUserMedia rejected: ${msg}`,
        { error_code: err?.name || null, reason: 'mic_denied' });
      if (onErrorRef.current) onErrorRef.current(msg);
      cleanupAll();
      // F4 — release the capture claim we took at the top of start().
      exitMode('capture');
    }
  }, [startMeter, cleanupAll, enterMode, exitMode, flushDraft, emit]);

  // TICKET-936 (CEO 2026-10-19): "the audio recording on the pwa should never get
  // lost if the app goes to the screen lock". Hidden: log it and put what we have
  // on disk now (iOS may freeze or kill the page next). Visible again with the
  // recorder stopped behind our back: ask the user (continue / save / discard).
  useEffect(() => {
    if (!recording) return undefined;
    const onHidden = () => {
      emit('dictation_hidden', 'ok', 'app hidden while recording — audio kept on this device',
        { scope_id: draftIdRef.current, reason: recorderRef.current?.state || 'none' });
      if (chunksRef.current.length > 0) flushDraft({ status: 'pending' });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { onHidden(); return; }
      const rec = recorderRef.current;
      if (rec && rec.state === 'recording') return;
      setInterrupted(true);
      emit('dictation_interrupted', 'error', 'recording stopped while the app was hidden (screen lock) — user asked',
        { scope_id: draftIdRef.current, chunks: chunksRef.current.length });
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHidden);
    };
  }, [recording, emit, flushDraft]);

  /**
   * POST one blob to the direct-transcribe endpoint. Resolves the transcript
   * text; throws with a user-facing message. Never touches draft state —
   * callers own the keep-or-delete decision.
   */
  const transcribeBlob = useCallback(async (blob, mimeType) => {
    const audio_base64 = await blobToBase64(blob);
    const auth = await getAuthHeaders();
    const resp = await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify({
        action: 'voice.transcribe_direct',
        audio_base64,
        mime_type: mimeType || 'audio/webm',
        language,
        context_type: 'chat_composer',
        context_hint: 'dictation',
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data?.error) {
      throw new Error(data?.error || `Transcribe failed (${resp.status})`);
    }
    const text = (data?.text || '').trim();
    if (!text) {
      throw new Error(data?.reason === 'hallucination_only'
        ? 'Nothing meaningful detected — try speaking closer to the mic.'
        : 'No transcript returned.');
    }
    return text;
  }, [language]);

  /**
   * Overflow transport for recordings too big for the JSON body: UVTP
   * (/api/voice-transcribe). Signed upload to long-recordings, then UVTP's
   * heavy path chunks it and polls to a transcript.
   *
   * TICKET-1134 (2026-10-12): this used to call voice.transcribe_for_chat,
   * which makes ONE whole-file STT call. Fine while Gemini (hours of audio)
   * answered; the day Google blocked the project, the Whisper fallback 400'd
   * a 26-minute dictation ("longer than 1400 seconds"). UVTP chunks, so no
   * single provider limit applies. forceHeavy: this transport only fires past
   * ~9m50s, so the chunked path is always the right one — and it does not
   * depend on a duration the recorder may not have reported.
   *
   * The draft in IndexedDB is NOT touched here: the caller keeps it until real
   * text comes back, exactly as with the direct transport. If we upload and
   * then the POST or the poll dies, the local copy is still the user's.
   */
  const transcribeViaStorage = useCallback(async (blob, mimeType, draftId) => {
    const auth = await getAuthHeaders();
    const authedFetch = (url, opts = {}) =>
      fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...auth } });

    const upResp = await authedFetch(`${UVTP}?action=upload-url`);
    const upload = await upResp.json().catch(() => ({}));
    if (!upResp.ok || !upload?.uploadUrl || !upload?.audioStorageKey) {
      throw new Error(upload?.error || `Upload URL failed (${upResp.status})`);
    }

    // Signed URL carries its own token — plain fetch, no auth header.
    const putResp = await fetch(upload.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType || 'audio/webm' },
      body: blob,
    });
    if (!putResp.ok) throw new Error(`Upload failed (${putResp.status})`);

    const resp = await authedFetch(UVTP, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        audioStorageKey: upload.audioStorageKey,
        mimeType: mimeType || 'audio/webm',
        language: language && language !== 'auto' ? language : undefined,
        source: 'assistant',
        forceHeavy: true,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data?.error) {
      throw new Error(data?.error || `Transcribe failed (${resp.status})`);
    }

    let raw = data?.text;
    if (data?.status === 'queued') {
      const poll = await pollUvtpRun({
        uvtpRunId: data.uvtpRunId,
        traceId: draftId || data.uvtpRunId,
        fetchImpl: authedFetch,
      });
      raw = poll?.fullTranscript || poll?.text;
    } else if (data?.status !== 'complete') {
      throw new Error(`Transcribe failed (unexpected status ${data?.status || resp.status})`);
    }

    const text = (raw || '').trim();
    if (!text) throw new Error('No transcript returned.');
    return text;
  }, [language]);

  /**
   * Shared tail for "we have a durable draft, try to turn it into text".
   * Success deletes the draft. Every failure keeps it and surfaces `pending`.
   * Picks the transport by size; the keep-or-delete rules are identical.
   */
  const attemptTranscription = useCallback(async (draftId, blob, mimeType, attempt = 0) => {
    if (!blob || blob.size === 0) {
      const msg = 'Empty recording — nothing to transcribe';
      setError(msg);
      emit('dictation_failed', 'error', msg, { scope_id: draftId, reason: 'empty_blob' });
      // Nothing to protect — an empty draft is not audio.
      if (draftId) await withDrafts(() => deleteDictationDraft(draftId), 'discard-empty');
      setPending(null);
      if (onErrorRef.current) onErrorRef.current(msg);
      return false;
    }

    // Past the standard-upload ceiling neither transport can carry it. Keep the
    // audio and say so — this is the ONE remaining size wall, and it costs the
    // user a download rather than their words.
    if (blob.size > MAX_STORAGE_BYTES) {
      const mb = (blob.size / 1024 / 1024).toFixed(1);
      const msg = `Recording too large to transcribe (${mb}MB) — saved on this device. Download it from the composer.`;
      setError(msg);
      emit('dictation_failed', 'error',
        `blob exceeds storage-path ceiling: ${blob.size}B > ${MAX_STORAGE_BYTES}B`,
        { scope_id: draftId, reason: 'oversize', bytes: blob.size });
      const kept = await withDrafts(
        () => updateDictationDraft(draftId, { status: 'pending', error_message: msg }),
        'keep-oversize',
      );
      setPending(kept || await withDrafts(() => getDictationDraft(draftId), 'read-oversize'));
      if (onErrorRef.current) onErrorRef.current(msg);
      return false;
    }

    // Transport choice is purely about the server's body limit; everything
    // below this line is identical for both.
    const transport = blob.size > MAX_DIRECT_BYTES ? 'storage' : 'direct';

    setTranscribing(true);
    try {
      emit('dictation_transcribe_start', 'ok', `transcribing via ${transport}`,
        { scope_id: draftId, transport, bytes: blob.size, attempt });
      const text = transport === 'storage'
        ? await transcribeViaStorage(blob, mimeType, draftId)
        : await transcribeBlob(blob, mimeType);
      // TICKET-936: the text is safe BEFORE the audio goes — even if the
      // composer that asked for it has already been closed.
      if (safetyScope) rememberDictation(text, safetyScope);
      // Only now is it safe to let go of the audio.
      if (draftId) await withDrafts(() => deleteDictationDraft(draftId), 'clear-on-success');
      setPending(null);
      emit('dictation_ok', 'ok', 'dictation transcribed',
        { scope_id: draftId, chars: text.length, attempt });
      if (onTranscriptRef.current) onTranscriptRef.current(text);
      return true;
    } catch (err) {
      const reason = err?.message || 'Network error during transcription';
      const msg = `${reason} — your recording is saved on this device.`;
      setError(msg);
      emit('dictation_failed', 'error', reason,
        { scope_id: draftId, reason: 'transcribe_error', attempt, bytes: blob.size });
      const kept = await withDrafts(() => updateDictationDraft(draftId, {
        status: 'pending',
        attempts: attempt + 1,
        error_message: reason,
      }), 'keep-on-error');
      setPending(kept || await withDrafts(() => getDictationDraft(draftId), 'read-on-error'));
      if (onErrorRef.current) onErrorRef.current(msg);
      return false;
    } finally {
      setTranscribing(false);
    }
  }, [transcribeBlob, transcribeViaStorage, withDrafts, emit, safetyScope]);

  const stop = useCallback(async () => {
    setInterrupted(false);
    const rec = recorderRef.current;
    if (!rec || rec.state === 'inactive') {
      // TICKET-327 (CEO 2026-07-22, "the email voice-to-text is not working"):
      // an inactive recorder does NOT mean an empty one. `micRegistry` auto-
      // releases every registered mic on visibilitychange === 'hidden' /
      // pagehide / beforeunload, and `releaseMic()` calls `recorder.stop()`
      // directly — behind this hook's back, so the `rec.onstop` handler below
      // (attached only inside stop()) never exists and this early return is
      // where the user lands when they tap the mic to finish. On mobile that
      // path fires constantly: the screen dims, a notification arrives, the
      // user glances at another app. The old code ran cleanupAll(), which
      // wipes chunksRef — i.e. it threw away the ENTIRE recording, emitted
      // nothing, and showed no toast. Prod telemetry for the incident is three
      // `dictation_started` rows and literally nothing after them.
      //
      // Salvage whatever the encoder already handed us and run it through the
      // SAME keep-or-delete tail as a normal stop, so the durability contract
      // (TICKET-324) covers this path too.
      const salvageId = draftIdRef.current;
      const salvageMime = mimeRef.current || 'audio/webm';
      let salvaged = null;
      if (chunksRef.current.length > 0) {
        try { salvaged = new Blob(chunksRef.current, { type: salvageMime }); }
        catch { salvaged = null; }
      }

      setRecording(false);
      cleanupMeter();
      cleanupStream();

      if (salvaged && salvaged.size > 0) {
        emit('dictation_external_stop', 'ok',
          'recorder was stopped externally (mic registry / tab hidden) — salvaging audio',
          { scope_id: salvageId, bytes: salvaged.size });
        try {
          if (salvageId) await flushDraft({ status: 'pending' });
          await attemptTranscription(salvageId, salvaged, salvageMime);
        } finally {
          chunksRef.current = [];
          draftIdRef.current = null;
          recorderRef.current = null;
          exitMode('capture');
        }
        return;
      }

      // TICKET-341: reaching here means the recorder was already inactive AND
      // we salvaged nothing — no buffered chunks. On mobile this is the
      // tab-frozen case: iOS froze the page on background before the first
      // encoder slice, so MediaRecorder never delivered any data and there is
      // genuinely nothing to transcribe. But staying SILENT is what turned it
      // into a soft-fail (a `dictation_started` row, then nothing, no toast).
      // Emit so we're never blind again, and tell the user plainly instead of
      // doing nothing.
      const lostMsg = "Recording didn't capture — if you switched apps or the screen locked, tap the mic and record again in one go.";
      emit('dictation_lost', 'error',
        'recorder inactive with no buffered audio — nothing to salvage (likely tab frozen/backgrounded mid-record)',
        { scope_id: salvageId, reason: 'inactive_empty' });
      if (onErrorRef.current) onErrorRef.current(lostMsg);

      cleanupAll();
      // F4 — release the capture claim. Handles the "start then
      // immediate stop" path that exits here before transcription.
      exitMode('capture');
      return;
    }

    const donePromise = new Promise((resolve, reject) => {
      rec.onstop = () => {
        try {
          const mime = mimeRef.current || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type: mime });
          resolve(blob);
        } catch (err) {
          reject(err);
        }
      };
    });
    try { rec.stop(); } catch { /* noop */ }
    setRecording(false);
    cleanupMeter();

    const draftId = draftIdRef.current;
    let blob;
    try { blob = await donePromise; }
    catch (err) {
      const msg = err?.message || 'Failed to assemble recording';
      setError(msg);
      emit('dictation_failed', 'error', `blob assembly failed: ${msg}`,
        { scope_id: draftId, reason: 'assemble_error' });
      // The timeslice flushes already on disk are the salvage here.
      if (draftId) {
        const kept = await withDrafts(
          () => updateDictationDraft(draftId, { status: 'pending', error_message: msg }),
          'keep-on-assemble-fail',
        );
        if (kept) setPending(kept);
      }
      cleanupStream();
      exitMode('capture'); // F4 — early return must release the claim
      if (onErrorRef.current) onErrorRef.current(msg);
      return;
    }
    cleanupStream();

    try {
      // THE INVARIANT: the complete blob is on disk before anything can fail.
      if (draftId) await flushDraft({ status: 'pending' });
      await attemptTranscription(draftId, blob, mimeRef.current || 'audio/webm');
    } finally {
      chunksRef.current = [];
      draftIdRef.current = null;
      // F4 — release the capture claim after the whole stop+transcribe
      // round-trip completes, success or failure. Placed in finally so
      // every code path returns mode to 'idle'.
      exitMode('capture');
    }
  }, [cleanupAll, cleanupMeter, cleanupStream, exitMode, flushDraft, attemptTranscription, withDrafts, emit]);

  const toggle = useCallback(() => {
    if (recording) return stop();
    return start();
  }, [recording, start, stop]);

  /** Interrupted: transcribe what we have, then keep recording (TICKET-936). */
  const continueInterrupted = useCallback(async () => {
    await stop();
    return start();
  }, [stop, start]);

  /** Interrupted: the user throws this recording away (the UI asks twice). */
  const discardInterrupted = useCallback(async () => {
    const id = draftIdRef.current;
    emit('dictation_discarded', 'ok', 'user discarded an interrupted recording', { scope_id: id, reason: 'interrupted' });
    setInterrupted(false);
    setRecording(false);
    cleanupAll();
    draftIdRef.current = null;
    exitMode('capture');
    if (id) await withDrafts(() => deleteDictationDraft(id), 'discard-interrupted');
  }, [cleanupAll, exitMode, withDrafts, emit]);

  /** Re-send a saved recording. Same keep-or-delete rules as the first try. */
  const retryPending = useCallback(async () => {
    if (!pending || transcribing) return false;
    const draft = await withDrafts(() => getDictationDraft(pending.draft_id), 'retry-read');
    if (!draft?.audio_blob) {
      // The row is gone (another tab, a cleared store). Don't leave a ghost.
      setPending(null);
      return false;
    }
    const blob = reviveBlob(draft.audio_blob, draft.mime_type);
    emit('dictation_retry', 'ok', 'retrying saved dictation',
      { scope_id: draft.draft_id, attempt: draft.attempts ?? 0 });
    return attemptTranscription(draft.draft_id, blob, draft.mime_type, draft.attempts ?? 0);
  }, [pending, transcribing, withDrafts, attemptTranscription, emit]);

  /** User explicitly throws the audio away. The ONLY delete-without-transcript. */
  const discardPending = useCallback(async () => {
    if (!pending) return;
    emit('dictation_discarded', 'ok', 'user discarded a saved dictation',
      { scope_id: pending.draft_id, bytes: pending.size_bytes ?? null });
    await withDrafts(() => deleteDictationDraft(pending.draft_id), 'discard');
    setPending(null);
    setError(null);
  }, [pending, withDrafts, emit]);

  /** Hand the audio back to the user as a file — the last-resort escape hatch. */
  const downloadPending = useCallback(async () => {
    if (!pending) return false;
    const draft = await withDrafts(() => getDictationDraft(pending.draft_id), 'download-read');
    if (!draft?.audio_blob) { setPending(null); return false; }
    const blob = reviveBlob(draft.audio_blob, draft.mime_type);
    const ext = (draft.mime_type || '').includes('mp4') ? 'm4a' : 'webm';
    const stamp = new Date(draft.created_at || Date.now())
      .toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dictation-${stamp}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke late — Safari cancels the download if the URL dies too early.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    emit('dictation_downloaded', 'ok', 'saved dictation downloaded to device',
      { scope_id: draft.draft_id, bytes: draft.size_bytes ?? null });
    return true;
  }, [pending, withDrafts, emit]);

  return {
    recording,
    transcribing,
    audioLevel,
    error,
    pending,
    draftsUnavailable,
    interrupted,
    continueInterrupted,
    discardInterrupted,
    start,
    stop,
    toggle,
    retryPending,
    discardPending,
    downloadPending,
  };
}
